'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';

import cx from 'classnames';
import { useInView, useIsomorphicLayoutEffect } from 'framer-motion';
import { useSessionStorage } from 'usehooks-ts';

import { type ExperienceBlock } from '~/contentful/graphql/_generated/graphql';
import { trackEvent } from '~/ui/components/analytics';
import { Button, ButtonSize, ButtonVariant } from '~/ui/components/button';
import { PlayPauseButton } from '~/ui/components/button/playPause';
import { Column, Grid } from '~/ui/components/grid';
import { Icon } from '~/ui/components/icon';
import richTextStyles from '~/ui/modules/richText/richText.module.scss';
import { Breakpoint } from '~/ui/styles/grid';
import { useVideoPlayback } from '~/v1/utils/useVideoPlayback';

import styles from './interactive.module.scss';
import { CACHE_NAME } from './preload';
import { Progress } from './progress';

export interface InteractiveProps {
  experienceBlock: ExperienceBlock;
}

export function Interactive({ experienceBlock }: InteractiveProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [videoIndex, setVideoIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(true);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [muted, setMuted] = useState(true);
  const metadataId = useId();

  const media = experienceBlock.referencesCollection?.items || [];

  const incrementCurrentVideo = useCallback(() => {
    setVideoIndex(prev => (prev + 1) % media.length);
  }, [media.length]);

  const decrementCurrentVideo = useCallback(() => {
    // https://web.archive.org/web/20090717035140if_/javascript.about.com/od/problemsolving/a/modulobug.htm
    setVideoIndex(prev => (((prev - 1) % media.length) + media.length) % media.length);
  }, [media.length]);

  const onVideoProgress = useCallback(
    (index: number, progress: number) => {
      if (index !== videoIndex) {
        return;
      }
      setProgress((index + progress) / media.length);
    },
    [videoIndex, media.length],
  );

  // keyboard navigation
  useIsomorphicLayoutEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        incrementCurrentVideo();
        event.preventDefault();
      } else if (event.key === 'ArrowLeft') {
        decrementCurrentVideo();
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [decrementCurrentVideo, incrementCurrentVideo]);

  // update progress when the video index changes
  useEffect(() => {
    setProgress(videoIndex / media.length);
    // if the video is paused and the user navigates to a different video, unpause it
    if (paused) {
      setPaused(false);
    }
    // we *dont* want to rerender when `progress` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.length, videoIndex]);
  const videoItem = media[videoIndex];

  const videoData = videoItem && 'video' in videoItem ? videoItem : null;

  return (
    <Grid
      className={cx(styles.root, 'module', richTextStyles.embeddedEntry)}
      ref={rootRef}
      fullWidth
    >
      <Column
        span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 7, [Breakpoint.DESKTOP]: 9 }}
        className={styles.column}
      >
        <div className={styles.bottomGradient} />
        <div className={styles.content}>
          <h3 className="modulesTitleTypography">{experienceBlock.json.title}</h3>
          <div className={styles.navigation}>
            <Button
              onClick={() => {
                decrementCurrentVideo();
                trackEvent('aapb_interactive_arrow_left');
              }}
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.ICON}
              aria-label="Previous video"
            >
              <Icon name="arrow-left" />
            </Button>
            <Button
              onClick={() => {
                incrementCurrentVideo();
                trackEvent('aapb_interactive_arrow_right');
              }}
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.ICON}
              aria-label="Next video"
            >
              <Icon name="arrow-right" />
            </Button>
          </div>

          <div className={styles.footer}>
            <Progress
              experienceBlock={experienceBlock}
              currentVideo={videoIndex}
              setCurrentVideo={setVideoIndex}
              progress={progress}
              metadataId={metadataId}
            />
            <div className={styles.controls}>
              <Button
                onClick={() => setCaptionsEnabled(prev => !prev)}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.ICON}
                aria-label={`${captionsEnabled ? 'Disable' : 'Enable'} closed captions`}
              >
                <Icon name={captionsEnabled ? 'cc-on' : 'cc-off'} />
              </Button>
              <Button
                onClick={() => {
                  setMuted(prev => !prev);
                  trackEvent('aapb_interactive_sound');
                }}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.ICON}
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                <Icon name={muted ? 'headphone-off' : 'headphone'} />
              </Button>
              <PlayPauseButton
                onClick={() => {
                  setPaused(prev => !prev);
                  trackEvent('aapb_interactive_pause');
                }}
                isPaused={paused}
                variant={ButtonVariant.PRIMARY_TRANSPARENT}
                ariaLabels={{ paused: 'Play', playing: 'Pause' }}
              />
            </div>
          </div>
        </div>
        <Video
          videoSrc={videoData?.video?.url ?? ''}
          metadataId={metadataId}
          captionsSrc={videoData?.closedCaptions?.url ?? ''}
          onProgress={progress => onVideoProgress(videoIndex, progress)}
          onEnded={incrementCurrentVideo}
          paused={paused}
          setPaused={setPaused}
          muted={muted}
          setMuted={setMuted}
          captionsEnabled={captionsEnabled}
        />
      </Column>
    </Grid>
  );
}

/**
 * This needs to be a separate component because light-react-grid doesn't render children until the
 * second render cycle. Because of this, `videoRef` will be undefined on the first render and we
 * can't set the video src.
 */
function Video({
  onProgress,
  onEnded,
  videoSrc,
  metadataId,
  captionsSrc,
  paused,
  setPaused,
  muted,
  setMuted,
  captionsEnabled,
}: {
  onProgress: (progress: number) => void;
  onEnded: () => void;
  videoSrc: string;
  metadataId: string;
  captionsSrc: string;
  paused: boolean;
  setPaused: React.Dispatch<React.SetStateAction<boolean>>;
  muted: boolean;
  setMuted: React.Dispatch<React.SetStateAction<boolean>>;
  captionsEnabled: boolean;
}) {
  const [blobUrls] = useSessionStorage<Record<string, string>>(CACHE_NAME, {});
  const videoRef = useRef<HTMLVideoElement>(null);
  const inView = useInView(videoRef, { once: true });

  useEffect(() => {
    if (inView) {
      setPaused(false);
    }
  }, [inView, setPaused]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (paused) {
      video.pause();
    } else {
      video.play();
    }
  }, [paused, videoSrc]);

  // pause all other videos on page when the interactive module is unmuted
  const onPlay = useVideoPlayback(videoRef, () => setMuted(true));
  useEffect(() => {
    if (!muted) {
      onPlay();
    }
  }, [muted, onPlay]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const onTimeUpdate = () => {
      onProgress(video.currentTime / video.duration);
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('play', onTimeUpdate);
  }, [onProgress]);

  return (
    <video
      src={blobUrls[videoSrc] ?? videoSrc}
      muted={muted}
      playsInline
      // we usually want to rely on onProgress to increment the video before it ends but per
      // this Bugzilla comment, the rate is variable and may be throttled if the cpu is busy
      // https://bugzilla.mozilla.org/show_bug.cgi?id=571822
      onEnded={onEnded}
      onClick={() => setPaused(prev => !prev)}
      aria-describedby={metadataId}
      className={styles.video}
      crossOrigin="anonymous"
      ref={videoRef}
    >
      <track
        src={captionsSrc}
        srcLang="en"
        label="English"
        kind="captions"
        ref={trackEl => {
          if (!trackEl) {
            return;
          }
          if (!trackEl.track.cues) {
            return;
          }
          for (const cue of trackEl.track.cues) {
            (cue as VTTCue).line = -6;
          }
          trackEl.track.mode = captionsEnabled ? 'showing' : 'hidden';
        }}
        default
      />
    </video>
  );
}
